import { designClient } from './client';

// Hand-written mirrors of NYCE Design's admin contracts (`NyceLogic.Design.Contracts/Admin/TenantAdminContracts.cs`,
// reference `openapi/design.json`); the hub does not generate them.

/** A row of Design's tenant registry; `id` is the AUTH tenant id (`tid`). */
export interface DesignTenant {
  id: string;
  code: string;
  name: string;
  /** Off = every Design API call of that tenant answers 403 `tenant_not_enabled` (within 30 s). */
  enabled: boolean;
  sso: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface DesignTenantCreate {
  id: string;
  code: string;
  name: string;
  enabled: boolean;
  sso: boolean;
}

/** A full replace of the admin fields. */
export type DesignTenantUpdate = Omit<DesignTenantCreate, 'id'>;

export interface DesignTenantUsage {
  tenantId: string;
  /** Documents per table, every collection present. */
  collections: Record<string, number>;
  total: number;
  lastWriteAt: string | null;
}

/** Design's `{error, code, field}`; codes `invalid_code`, `invalid_name`, `invalid_id`, `id_taken`, `code_taken`, … */
export interface DesignError {
  error: string;
  code: string;
  field?: string | null;
}

/** The sign-in code rule (Design backend and SPA): trimmed, lowercase, 2–32 of a-z 0-9 -, no hyphen at either end. */
export const CODE_RX = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;
export const normalizeCode = (s: string) => s.trim().toLowerCase();

/** A code suggestion from an AUTH tenant name: `Stadium test AB` → `stadium-test-ab`. */
export const codeFrom = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/, '');

export function designTenants(target: string) {
  const c = designClient(target);
  const enc = encodeURIComponent;
  return {
    list: () => c.request<DesignTenant[]>('GET', '/tenants'),
    get: (id: string) => c.request<DesignTenant>('GET', `/tenants/${enc(id)}`),
    usage: (id: string) => c.request<DesignTenantUsage>('GET', `/tenants/${enc(id)}/usage`),
    create: (body: DesignTenantCreate) => c.request<DesignTenant>('POST', '/tenants', body),
    update: (id: string, body: DesignTenantUpdate) =>
      c.request<DesignTenant>('PUT', `/tenants/${enc(id)}`, body),
  };
}
